import { apiClient } from './apiClient';

export type TechnologyCatalogCategory =
  | 'Software'
  | 'Database'
  | 'Framework'
  | 'CloudService'
  | 'OperatingSystem'
  | 'Library'
  | 'AiModel'
  | 'ProgrammingLanguage'
  | 'Other';

export type TechnologyApplicationStatus = 'Draft' | 'Active' | 'Deprecated' | 'Retired';

export type TechnologyEnvironmentKind =
  | 'Development'
  | 'Homologation'
  | 'Production'
  | 'DisasterRecovery';

export type TechnologySecretKind =
  | 'Password'
  | 'Token'
  | 'ApiKey'
  | 'OAuth'
  | 'Jwt'
  | 'License'
  | 'Other';

export type TechnologyInfrastructureKind =
  | 'Server'
  | 'Cloud'
  | 'Database'
  | 'Storage'
  | 'Cdn'
  | 'LoadBalancer'
  | 'Network'
  | 'Other';

export type TechnologyAccessKeyKind = 'Ssh' | 'Api' | 'MachineIdentity' | 'Other';

export type TechnologyAccessKeyStatus = 'Active' | 'PendingRotation' | 'Revoked' | 'Expired';

export type TechnologyListResult<T> = {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type TechnologyCatalogItem = {
  id: string;
  name: string;
  category: TechnologyCatalogCategory | string;
  version?: string | null;
  vendor?: string | null;
  licenseType?: string | null;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyCatalogItemUpsertPayload = {
  name: string;
  category: string;
  version?: string | null;
  vendor?: string | null;
  licenseType?: string | null;
  ownerUserId?: string | null;
  notes?: string | null;
};

export type TechnologyInfrastructureAsset = {
  id: string;
  name: string;
  kind: TechnologyInfrastructureKind | string;
  vendor?: string | null;
  ipAddress?: string | null;
  operatingSystem?: string | null;
  cpuCores?: number | null;
  ramGb?: number | null;
  diskGb?: number | null;
  region?: string | null;
  currency?: string | null;
  monthlyCost?: number | null;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyInfrastructureAssetUpsertPayload = {
  name: string;
  kind: string;
  vendor?: string | null;
  ipAddress?: string | null;
  operatingSystem?: string | null;
  cpuCores?: number | null;
  ramGb?: number | null;
  diskGb?: number | null;
  region?: string | null;
  currency?: string | null;
  monthlyCost?: number | null;
  notes?: string | null;
};

export type TechnologyAccessKey = {
  id: string;
  name: string;
  kind: TechnologyAccessKeyKind | string;
  status: TechnologyAccessKeyStatus | string;
  fingerprint?: string | null;
  publicKey?: string | null;
  scope?: string | null;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  lastRotatedAtUtc?: string | null;
  expiresAtUtc?: string | null;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyAccessKeyUpsertPayload = {
  name: string;
  kind: string;
  status: string;
  fingerprint?: string | null;
  publicKey?: string | null;
  scope?: string | null;
  ownerUserId?: string | null;
  lastRotatedAtUtc?: string | null;
  expiresAtUtc?: string | null;
  notes?: string | null;
};

export type TechnologyCatalogItemRef = {
  id: string;
  linkId: string;
  name: string;
  category: TechnologyCatalogCategory | string;
};

export type TechnologyApplicationHosting = {
  id: string;
  applicationId: string;
  applicationName: string;
  environmentKind: string;
  address: string;
  infrastructureAssetId: string;
  infrastructureAssetName: string;
  infrastructureVendor?: string | null;
  infrastructureIpAddress?: string | null;
  createdAtUtc: string;
};

export type TechnologyApplicationHostingUpsertPayload = {
  environmentKind: string;
  infrastructureAssetId: string;
  address?: string | null;
};

export type TechnologyApplication = {
  id: string;
  catalogItems: TechnologyCatalogItemRef[];
  name: string;
  version?: string | null;
  repositoryUrl?: string | null;
  pipelineUrl?: string | null;
  azureDevOpsOrganization?: string | null;
  azureDevOpsProject?: string | null;
  azureDevOpsRepository?: string | null;
  azureDevOpsPatSecretId?: string | null;
  azureDevOpsPatSecretName?: string | null;
  status: TechnologyApplicationStatus | string;
  ownerUserId?: string | null;
  ownerUserName?: string | null;
  notes?: string | null;
  hostingCount: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyApplicationUpsertPayload = {
  catalogItemIds: string[];
  name: string;
  version?: string | null;
  repositoryUrl?: string | null;
  pipelineUrl?: string | null;
  azureDevOpsOrganization?: string | null;
  azureDevOpsProject?: string | null;
  azureDevOpsRepository?: string | null;
  azureDevOpsPatSecretId?: string | null;
  status: string;
  ownerUserId?: string | null;
  notes?: string | null;
};

export type TechnologySecretBinding = {
  id: string;
  applicationId?: string | null;
  applicationName?: string | null;
  environmentKind?: string | null;
  environmentName?: string | null;
  purpose?: string | null;
};

export type TechnologySecretBindingUpsertPayload = {
  applicationId?: string | null;
  environmentKind?: string | null;
  purpose?: string | null;
};

export type TechnologySecret = {
  id: string;
  name: string;
  kind: TechnologySecretKind | string;
  version: number;
  expiresAtUtc?: string | null;
  notes?: string | null;
  hasValue: boolean;
  bindings: TechnologySecretBinding[];
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologySecretUpsertPayload = {
  name: string;
  kind: string;
  value?: string | null;
  expiresAtUtc?: string | null;
  notes?: string | null;
  bindings: TechnologySecretBindingUpsertPayload[];
};

export type TechnologySecretReveal = {
  id: string;
  name: string;
  kind: string;
  value: string;
  version: number;
};

export async function listTechnologyCatalogItems(params: {
  category?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyCatalogItem>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyCatalogItem>>(
    '/v1/technology/catalog',
    { params },
  );
  return response.data;
}

export async function createTechnologyCatalogItem(
  payload: TechnologyCatalogItemUpsertPayload,
): Promise<TechnologyCatalogItem> {
  const response = await apiClient.post<TechnologyCatalogItem>('/v1/technology/catalog', payload);
  return response.data;
}

export async function updateTechnologyCatalogItem(
  id: string,
  payload: TechnologyCatalogItemUpsertPayload,
): Promise<TechnologyCatalogItem> {
  const response = await apiClient.put<TechnologyCatalogItem>(`/v1/technology/catalog/${id}`, payload);
  return response.data;
}

export async function deleteTechnologyCatalogItem(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/catalog/${id}`);
}

export async function listTechnologyInfrastructureAssets(params: {
  kind?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyInfrastructureAsset>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyInfrastructureAsset>>(
    '/v1/technology/infrastructure',
    { params },
  );
  return response.data;
}

export async function createTechnologyInfrastructureAsset(
  payload: TechnologyInfrastructureAssetUpsertPayload,
): Promise<TechnologyInfrastructureAsset> {
  const response = await apiClient.post<TechnologyInfrastructureAsset>(
    '/v1/technology/infrastructure',
    payload,
  );
  return response.data;
}

export async function updateTechnologyInfrastructureAsset(
  id: string,
  payload: TechnologyInfrastructureAssetUpsertPayload,
): Promise<TechnologyInfrastructureAsset> {
  const response = await apiClient.put<TechnologyInfrastructureAsset>(
    `/v1/technology/infrastructure/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteTechnologyInfrastructureAsset(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/infrastructure/${id}`);
}

export async function listTechnologyAccessKeys(params: {
  kind?: string;
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyAccessKey>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyAccessKey>>(
    '/v1/technology/keys',
    { params },
  );
  return response.data;
}

export async function createTechnologyAccessKey(
  payload: TechnologyAccessKeyUpsertPayload,
): Promise<TechnologyAccessKey> {
  const response = await apiClient.post<TechnologyAccessKey>('/v1/technology/keys', payload);
  return response.data;
}

export async function updateTechnologyAccessKey(
  id: string,
  payload: TechnologyAccessKeyUpsertPayload,
): Promise<TechnologyAccessKey> {
  const response = await apiClient.put<TechnologyAccessKey>(`/v1/technology/keys/${id}`, payload);
  return response.data;
}

export async function deleteTechnologyAccessKey(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/keys/${id}`);
}

export async function getTechnologyApplication(id: string): Promise<TechnologyApplication> {
  const response = await apiClient.get<TechnologyApplication>(`/v1/technology/applications/${id}`);
  return response.data;
}

export async function listTechnologyApplications(params: {
  status?: string;
  catalogItemId?: string;
  salesProductId?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyApplication>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyApplication>>(
    '/v1/technology/applications',
    { params },
  );
  return response.data;
}

export async function createTechnologyApplication(
  payload: TechnologyApplicationUpsertPayload,
): Promise<TechnologyApplication> {
  const response = await apiClient.post<TechnologyApplication>('/v1/technology/applications', payload);
  return response.data;
}

export async function updateTechnologyApplication(
  id: string,
  payload: TechnologyApplicationUpsertPayload,
): Promise<TechnologyApplication> {
  const response = await apiClient.put<TechnologyApplication>(
    `/v1/technology/applications/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteTechnologyApplication(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/applications/${id}`);
}

export async function listTechnologyApplicationHostings(
  applicationId: string,
): Promise<TechnologyApplicationHosting[]> {
  const response = await apiClient.get<TechnologyApplicationHosting[]>(
    `/v1/technology/applications/${applicationId}/hostings`,
  );
  return response.data;
}

export async function createTechnologyApplicationHosting(
  applicationId: string,
  payload: TechnologyApplicationHostingUpsertPayload,
): Promise<TechnologyApplicationHosting> {
  const response = await apiClient.post<TechnologyApplicationHosting>(
    `/v1/technology/applications/${applicationId}/hostings`,
    payload,
  );
  return response.data;
}

export async function updateTechnologyApplicationHosting(
  applicationId: string,
  id: string,
  payload: TechnologyApplicationHostingUpsertPayload,
): Promise<TechnologyApplicationHosting> {
  const response = await apiClient.put<TechnologyApplicationHosting>(
    `/v1/technology/applications/${applicationId}/hostings/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteTechnologyApplicationHosting(
  applicationId: string,
  id: string,
): Promise<void> {
  await apiClient.delete(`/v1/technology/applications/${applicationId}/hostings/${id}`);
}

export async function listTechnologySecrets(params: {
  kind?: string;
  applicationId?: string;
  environmentKind?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologySecret>> {
  const response = await apiClient.get<TechnologyListResult<TechnologySecret>>(
    '/v1/technology/secrets',
    { params },
  );
  return response.data;
}

export async function createTechnologySecret(
  payload: TechnologySecretUpsertPayload,
): Promise<TechnologySecret> {
  const response = await apiClient.post<TechnologySecret>('/v1/technology/secrets', payload);
  return response.data;
}

export async function updateTechnologySecret(
  id: string,
  payload: TechnologySecretUpsertPayload,
): Promise<TechnologySecret> {
  const response = await apiClient.put<TechnologySecret>(`/v1/technology/secrets/${id}`, payload);
  return response.data;
}

export async function deleteTechnologySecret(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/secrets/${id}`);
}

export async function revealTechnologySecret(id: string): Promise<TechnologySecretReveal> {
  const response = await apiClient.get<TechnologySecretReveal>(`/v1/technology/secrets/${id}/reveal`);
  return response.data;
}

export type TechnologyAiDataClassification = 'None' | 'Public' | 'Personal' | 'Sensitive';

export type TechnologyAiAutonomyLevel =
  | 'Assistive'
  | 'Recommendation'
  | 'DecisionSupport'
  | 'AutomatedWithReview'
  | 'AutomatedWithoutReview';

export type TechnologyAiSubjectImpact = 'Low' | 'Medium' | 'High' | 'Critical';

export type TechnologyAiProcessCriticality = 'Low' | 'Medium' | 'High' | 'Critical';

export type TechnologyAiRiskClass = 'Minimal' | 'Limited' | 'High' | 'Unacceptable';

export type TechnologyAiUsageStatus = 'Active' | 'Retired';

export type TechnologyAiUsage = {
  id: string;
  name: string;
  applicationId: string;
  applicationName?: string | null;
  catalogItemId?: string | null;
  catalogItemName?: string | null;
  salesProductId?: string | null;
  salesProductName?: string | null;
  businessProcessName: string;
  purpose: string;
  dataClassification: TechnologyAiDataClassification | string;
  autonomyLevel: TechnologyAiAutonomyLevel | string;
  subjectImpact: TechnologyAiSubjectImpact | string;
  processCriticality: TechnologyAiProcessCriticality | string;
  riskClass: TechnologyAiRiskClass | string;
  rationale: string;
  assessedByUserId: string;
  assessedByUserName?: string | null;
  assessedAtUtc: string;
  nextReviewAtUtc: string;
  status: TechnologyAiUsageStatus | string;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyAiUsageUpsertPayload = {
  name: string;
  applicationId: string;
  catalogItemId?: string | null;
  salesProductId?: string | null;
  businessProcessName: string;
  purpose: string;
  dataClassification: string;
  autonomyLevel: string;
  subjectImpact: string;
  processCriticality: string;
  rationale: string;
  nextReviewAtUtc: string;
  status: string;
};

export async function listTechnologyAiUsages(params: {
  riskClass?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyAiUsage>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyAiUsage>>(
    '/v1/technology/ai/usages',
    { params },
  );
  return response.data;
}

export async function classifyTechnologyAiUsage(payload: {
  dataClassification: string;
  autonomyLevel: string;
  subjectImpact: string;
  processCriticality: string;
}): Promise<{ riskClass: TechnologyAiRiskClass | string }> {
  const response = await apiClient.post<{ riskClass: TechnologyAiRiskClass | string }>(
    '/v1/technology/ai/usages/classify',
    payload,
  );
  return response.data;
}

export async function createTechnologyAiUsage(
  payload: TechnologyAiUsageUpsertPayload,
): Promise<TechnologyAiUsage> {
  const response = await apiClient.post<TechnologyAiUsage>('/v1/technology/ai/usages', payload);
  return response.data;
}

export async function updateTechnologyAiUsage(
  id: string,
  payload: TechnologyAiUsageUpsertPayload,
): Promise<TechnologyAiUsage> {
  const response = await apiClient.put<TechnologyAiUsage>(`/v1/technology/ai/usages/${id}`, payload);
  return response.data;
}

export async function deleteTechnologyAiUsage(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/ai/usages/${id}`);
}

export type TechnologyAiPrompt = {
  id: string;
  name: string;
  usageId: string;
  usageName?: string | null;
  catalogItemId?: string | null;
  catalogItemName?: string | null;
  fallbackCatalogItemId?: string | null;
  fallbackCatalogItemName?: string | null;
  body: string;
  temperature?: number | null;
  version: number;
  datasetNotes?: string | null;
  status: TechnologyAiUsageStatus | string;
  createdByUserId: string;
  createdByUserName?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyAiPromptUpsertPayload = {
  name: string;
  usageId: string;
  catalogItemId?: string | null;
  fallbackCatalogItemId?: string | null;
  body: string;
  temperature?: number | null;
  datasetNotes?: string | null;
  status: string;
};

export type TechnologyAiBenchmark = {
  id: string;
  name: string;
  usageId: string;
  usageName?: string | null;
  promptId?: string | null;
  promptName?: string | null;
  catalogItemId?: string | null;
  catalogItemName?: string | null;
  datasetName: string;
  qualityScore?: number | null;
  latencyMs?: number | null;
  estimatedCost?: number | null;
  result: string;
  recommendation?: string | null;
  evaluatedAtUtc: string;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyAiBenchmarkUpsertPayload = {
  name: string;
  usageId: string;
  promptId?: string | null;
  catalogItemId?: string | null;
  datasetName: string;
  qualityScore?: number | null;
  latencyMs?: number | null;
  estimatedCost?: number | null;
  result: string;
  recommendation?: string | null;
  evaluatedAtUtc: string;
};

export type TechnologyAiCost = {
  id: string;
  usageId: string;
  usageName?: string | null;
  periodYear: number;
  periodMonth: number;
  tokenCount?: number | null;
  amount: number;
  currency: string;
  budgetAmount?: number | null;
  overBudget: boolean;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyAiCostUpsertPayload = {
  usageId: string;
  periodYear: number;
  periodMonth: number;
  tokenCount?: number | null;
  amount: number;
  currency: string;
  budgetAmount?: number | null;
  notes?: string | null;
};

export async function listTechnologyAiPrompts(params: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyAiPrompt>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyAiPrompt>>(
    '/v1/technology/ai/prompts',
    { params },
  );
  return response.data;
}

export async function createTechnologyAiPrompt(
  payload: TechnologyAiPromptUpsertPayload,
): Promise<TechnologyAiPrompt> {
  const response = await apiClient.post<TechnologyAiPrompt>('/v1/technology/ai/prompts', payload);
  return response.data;
}

export async function updateTechnologyAiPrompt(
  id: string,
  payload: TechnologyAiPromptUpsertPayload,
): Promise<TechnologyAiPrompt> {
  const response = await apiClient.put<TechnologyAiPrompt>(`/v1/technology/ai/prompts/${id}`, payload);
  return response.data;
}

export async function deleteTechnologyAiPrompt(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/ai/prompts/${id}`);
}

export async function listTechnologyAiBenchmarks(params: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyAiBenchmark>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyAiBenchmark>>(
    '/v1/technology/ai/benchmarks',
    { params },
  );
  return response.data;
}

export async function createTechnologyAiBenchmark(
  payload: TechnologyAiBenchmarkUpsertPayload,
): Promise<TechnologyAiBenchmark> {
  const response = await apiClient.post<TechnologyAiBenchmark>(
    '/v1/technology/ai/benchmarks',
    payload,
  );
  return response.data;
}

export async function updateTechnologyAiBenchmark(
  id: string,
  payload: TechnologyAiBenchmarkUpsertPayload,
): Promise<TechnologyAiBenchmark> {
  const response = await apiClient.put<TechnologyAiBenchmark>(
    `/v1/technology/ai/benchmarks/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteTechnologyAiBenchmark(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/ai/benchmarks/${id}`);
}

export async function listTechnologyAiCosts(params: {
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyAiCost>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyAiCost>>(
    '/v1/technology/ai/costs',
    { params },
  );
  return response.data;
}

export async function createTechnologyAiCost(
  payload: TechnologyAiCostUpsertPayload,
): Promise<TechnologyAiCost> {
  const response = await apiClient.post<TechnologyAiCost>('/v1/technology/ai/costs', payload);
  return response.data;
}

export async function updateTechnologyAiCost(
  id: string,
  payload: TechnologyAiCostUpsertPayload,
): Promise<TechnologyAiCost> {
  const response = await apiClient.put<TechnologyAiCost>(`/v1/technology/ai/costs/${id}`, payload);
  return response.data;
}

export async function deleteTechnologyAiCost(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/ai/costs/${id}`);
}

export type TechnologyIntegration = {
  id: string;
  name: string;
  vendor: string;
  kind: string;
  authKind: string;
  endpoint?: string | null;
  scope?: string | null;
  rateLimit?: string | null;
  sla?: string | null;
  status: string;
  documentationUrl?: string | null;
  applicationId: string;
  applicationName?: string | null;
  secretId?: string | null;
  secretName?: string | null;
  accessKeyId?: string | null;
  accessKeyName?: string | null;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyIntegrationUpsertPayload = {
  name: string;
  vendor: string;
  kind: string;
  authKind: string;
  endpoint?: string | null;
  scope?: string | null;
  rateLimit?: string | null;
  sla?: string | null;
  status: string;
  documentationUrl?: string | null;
  applicationId: string;
  secretId?: string | null;
  accessKeyId?: string | null;
  notes?: string | null;
};

export type TechnologyCertificate = {
  id: string;
  name: string;
  kind: string;
  issuer?: string | null;
  subjectName: string;
  expiresAtUtc: string;
  status: string;
  expirationState: string;
  applicationId: string;
  applicationName?: string | null;
  environmentKind?: string | null;
  environmentName?: string | null;
  infrastructureAssetId?: string | null;
  infrastructureAssetName?: string | null;
  secretId?: string | null;
  secretName?: string | null;
  notes?: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type TechnologyCertificateUpsertPayload = {
  name: string;
  kind: string;
  issuer?: string | null;
  subjectName: string;
  expiresAtUtc: string;
  status: string;
  applicationId: string;
  environmentKind?: string | null;
  infrastructureAssetId?: string | null;
  secretId?: string | null;
  notes?: string | null;
};

export async function listTechnologyIntegrations(params: {
  status?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyIntegration>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyIntegration>>(
    '/v1/technology/integrations',
    { params },
  );
  return response.data;
}

export async function createTechnologyIntegration(
  payload: TechnologyIntegrationUpsertPayload,
): Promise<TechnologyIntegration> {
  const response = await apiClient.post<TechnologyIntegration>('/v1/technology/integrations', payload);
  return response.data;
}

export async function updateTechnologyIntegration(
  id: string,
  payload: TechnologyIntegrationUpsertPayload,
): Promise<TechnologyIntegration> {
  const response = await apiClient.put<TechnologyIntegration>(
    `/v1/technology/integrations/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteTechnologyIntegration(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/integrations/${id}`);
}

export async function listTechnologyCertificates(params: {
  expiration?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}): Promise<TechnologyListResult<TechnologyCertificate>> {
  const response = await apiClient.get<TechnologyListResult<TechnologyCertificate>>(
    '/v1/technology/certificates',
    { params },
  );
  return response.data;
}

export async function createTechnologyCertificate(
  payload: TechnologyCertificateUpsertPayload,
): Promise<TechnologyCertificate> {
  const response = await apiClient.post<TechnologyCertificate>(
    '/v1/technology/certificates',
    payload,
  );
  return response.data;
}

export async function updateTechnologyCertificate(
  id: string,
  payload: TechnologyCertificateUpsertPayload,
): Promise<TechnologyCertificate> {
  const response = await apiClient.put<TechnologyCertificate>(
    `/v1/technology/certificates/${id}`,
    payload,
  );
  return response.data;
}

export async function deleteTechnologyCertificate(id: string): Promise<void> {
  await apiClient.delete(`/v1/technology/certificates/${id}`);
}

export type DailyCount = {
  count: number;
};

export async function getTechnologyCatalogCount(): Promise<number> {
  const response = await apiClient.get<DailyCount>('/v1/technology/catalog/count');
  return response.data.count;
}

export async function getTechnologyApplicationCount(): Promise<number> {
  const response = await apiClient.get<DailyCount>('/v1/technology/applications/count');
  return response.data.count;
}

export async function getTechnologyServerCount(): Promise<number> {
  const response = await apiClient.get<DailyCount>('/v1/technology/infrastructure/count', {
    params: { kind: 'Server' },
  });
  return response.data.count;
}

export async function getTechnologyIntegrationCount(): Promise<number> {
  const response = await apiClient.get<DailyCount>('/v1/technology/integrations/count');
  return response.data.count;
}

export type TechnologyOverviewCompositionOption = {
  salesProductId: string;
  name: string;
  applicationCount: number;
};

export type TechnologyOverviewHosting = {
  id: string;
  environmentKind: string;
  address?: string | null;
  infrastructureAssetId: string;
  infrastructureAssetName: string;
  infrastructureVendor?: string | null;
  infrastructureKind: string;
};

export type TechnologyOverviewIntegration = {
  id: string;
  name: string;
  endpoint?: string | null;
  status: string;
};

export type TechnologyOverviewApplication = {
  id: string;
  name: string;
  version?: string | null;
  status: string;
  repositoryUrl?: string | null;
  notes?: string | null;
  languages: string[];
  hostings: TechnologyOverviewHosting[];
  integrations: TechnologyOverviewIntegration[];
};

export type TechnologyOverviewComposition = {
  salesProductId: string;
  name: string;
  applications: TechnologyOverviewApplication[];
};

export type TechnologyOverview = {
  compositions: TechnologyOverviewCompositionOption[];
  composition?: TechnologyOverviewComposition | null;
};

export async function getTechnologyOverview(salesProductId?: string): Promise<TechnologyOverview> {
  const response = await apiClient.get<TechnologyOverview>('/v1/technology/overview', {
    params: salesProductId ? { salesProductId } : undefined,
  });
  return response.data;
}

export type TechnologyDistributionProduct = {
  id: string;
  name: string;
};

export type TechnologyDistributionHosting = {
  id: string;
  applicationId: string;
  applicationName: string;
  environmentKind: string;
  address?: string | null;
  products: TechnologyDistributionProduct[];
};

export type TechnologyDistributionAsset = {
  id: string;
  name: string;
  kind: string;
  vendor?: string | null;
  ipAddress?: string | null;
  operatingSystem?: string | null;
  region?: string | null;
  cpuCores?: number | null;
  cpuUtilizationPercent?: number | null;
  ramGb?: number | null;
  memoryUtilizationPercent?: number | null;
  diskGb?: number | null;
  diskUtilizationPercent?: number | null;
  hostings: TechnologyDistributionHosting[];
};

export type TechnologyDistribution = {
  assets: TechnologyDistributionAsset[];
  products: TechnologyDistributionProduct[];
};

export async function getTechnologyDistribution(): Promise<TechnologyDistribution> {
  const response = await apiClient.get<TechnologyDistribution>('/v1/technology/overview/distribution');
  return response.data;
}
